import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  BROKER_REQUEST_ITEM_STATUS_LABELS_RU,
  type BrokerAnswerRow,
  type BrokerLineOptionDto,
} from '@crm/shared'
import { searchBrokerLines, uploadBrokerAnswer } from '../../../shared/api/customs.api'
import { apiErrorText } from '../../../shared/api/errorText'
import { brokerUploadSummary, positionsWord, type BrokerUploadOutcome } from './brokerActions'
import { BrokerBindRow } from './BrokerBindRow'

/**
 * Итог загрузки ответа брокера (с 30.10.2026, просьба Сергея «не должно тихо
 * отваливаться»): что легло, какие артикулы брокер записал по-своему и мы их
 * узнали, какие позиции не получили ответ из-за поправленного партномера и какие
 * артикулы не нашлись — их можно привязать к строке запроса вручную.
 */
export function BrokerUploadResultModal({
  outcome,
  onClose,
}: {
  outcome: BrokerUploadOutcome
  onClose: () => void
}) {
  const { result } = outcome
  const summary = brokerUploadSummary(outcome)
  const aliased = result.aliased ?? []
  const notApplied = result.notApplied ?? []
  // Позиции без ответа для выпадающих списков; привязанная в этом окне из списков уходит.
  const [bound, setBound] = useState<Set<string>>(() => new Set())
  const markBound = (itemId: string) => setBound((prev) => new Set(prev).add(itemId))
  const open = (result.openPositions ?? []).filter((o) => !bound.has(o.itemId))
  const openIds = new Set((result.openPositions ?? []).map((o) => o.itemId))
  // Позиция, привязанная в своей строке, не пропадает из её же списка.
  const optionsFor = (itemId?: string) =>
    (result.openPositions ?? []).filter((o) => !bound.has(o.itemId) || o.itemId === itemId)

  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', h)
    return () => window.removeEventListener('keydown', h)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-label="Итог загрузки ответа брокера"
        className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-3xl mx-4 max-h-[85vh] flex flex-col"
      >
        <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">Итог загрузки ответа брокера</h3>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            ×
          </button>
        </div>
        <div className="p-4 overflow-y-auto space-y-4 text-sm">
          <p
            className={
              summary.tone === 'error'
                ? 'text-red-600'
                : summary.tone === 'success'
                  ? 'text-green-700'
                  : 'text-gray-800'
            }
          >
            {summary.text}
          </p>

          {aliased.length > 0 && (
            <Section title={`Брокер записал артикул иначе — узнали сами (${aliased.length})`}>
              {aliased.map((a) => (
                <li key={`${a.filePart}|${a.requestPart}`}>
                  <span className="text-gray-500">в файле</span> {a.filePart}{' '}
                  <span className="text-gray-500">→ наш партномер</span>{' '}
                  <span className="font-medium">{a.requestPart}</span>
                  {a.fileCode && <span className="text-gray-400"> · {a.fileCode}</span>}
                </li>
              ))}
            </Section>
          )}

          {notApplied.length > 0 && (
            <Section title={`Ответ не лёг на позиции (${notApplied.length})`} tone="warn">
              {notApplied.map((n) =>
                n.reason === 'PART_CHANGED' && openIds.has(n.itemId) ? (
                  <BrokerBindRow
                    key={`${n.itemId}|${n.sentPartNumber}`}
                    brokerPart={n.sentPartNumber}
                    row={rowForPart(outcome, n.sentPartNumber)}
                    options={optionsFor(n.itemId)}
                    preselect={n.itemId}
                    tone="warn"
                    onBound={markBound}
                  />
                ) : (
                  <li key={`${n.itemId}|${n.sentPartNumber}`}>
                    {n.requestNumber ?? 'заявка без номера'}: брокеру ушёл{' '}
                    <span className="font-medium">{n.sentPartNumber}</span>
                    {n.reason === 'PART_CHANGED' ? (
                      <>
                        , а сейчас у позиции <span className="font-medium">{n.partNumber}</span> —
                        менеджер поправил партномер после отправки. Отправьте позицию брокеру заново
                        или впишите ответ вручную.
                      </>
                    ) : (
                      <> — позиция больше не из-за рубежа, ответ не записан.</>
                    )}
                  </li>
                ),
              )}
            </Section>
          )}

          {result.unmatched.length > 0 && (
            <Section
              title={`Не найдено в запросах (${result.unmatched.length}) — можно привязать вручную`}
              tone="warn"
            >
              {result.unmatched.map((part) =>
                (result.openPositions ?? []).length > 0 ? (
                  <BrokerBindRow
                    key={part}
                    brokerPart={part}
                    row={findRow(outcome.rows, part)}
                    options={open}
                    onBound={markBound}
                    fallback={<UnmatchedRow part={part} row={findRow(outcome.rows, part)} />}
                  />
                ) : (
                  <UnmatchedRow key={part} part={part} row={findRow(outcome.rows, part)} />
                ),
              )}
            </Section>
          )}
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex justify-end">
          <button
            onClick={onClose}
            className="px-3 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700"
          >
            Готово
          </button>
        </div>
      </div>
    </div>
  )
}

function Section({
  title,
  tone,
  children,
}: {
  title: string
  tone?: 'warn'
  children: React.ReactNode
}) {
  return (
    <section className="space-y-1.5">
      <h4
        className={`text-xs font-semibold ${tone === 'warn' ? 'text-amber-700' : 'text-gray-600'}`}
      >
        {title}
      </h4>
      <ul className="space-y-1.5 text-xs text-gray-800">{children}</ul>
    </section>
  )
}

function findRow(rows: BrokerAnswerRow[], part: string): BrokerAnswerRow | null {
  return (
    rows.find((r) => r.partNumber === part) ??
    rows.find((r) => r.partNumber.trim() === part.trim()) ??
    null
  )
}

/**
 * Строка файла по партномеру, ушедшему брокеру: брокер мог записать его иначе
 * (тогда он в «узнали сами»), или с другими пробелами и регистром.
 */
function rowForPart(outcome: BrokerUploadOutcome, sent: string): BrokerAnswerRow | null {
  const alias = (outcome.result.aliased ?? []).find((a) => a.requestPart === sent)
  const loose = (v: string) => v.toUpperCase().replace(/[^0-9A-ZА-ЯЁ]/g, '')
  return (
    findRow(outcome.rows, sent) ??
    (alias ? findRow(outcome.rows, alias.filePart) : null) ??
    outcome.rows.find((r) => loose(r.partNumber) === loose(sent)) ??
    null
  )
}

/** Для поиска — самое «узнаваемое» слово артикула: с цифрами и подлиннее. */
function searchSeed(part: string): string {
  const words = part.split(/[^0-9A-Za-zА-Яа-яЁё]+/).filter((w) => w.length >= 3)
  const withDigits = words.filter((w) => /\d/.test(w)).sort((a, b) => b.length - a.length)
  return withDigits[0] ?? words[0] ?? part
}

/** Ненайденный артикул: поиск строки запроса и привязка — строка файла грузится ещё раз с нашим партномером. */
function UnmatchedRow({ part, row }: { part: string; row: BrokerAnswerRow | null }) {
  const qc = useQueryClient()
  const [q, setQ] = useState(() => searchSeed(part))
  const [picked, setPicked] = useState<BrokerLineOptionDto | null>(null)
  const [state, setState] = useState<{ busy?: boolean; done?: string; error?: string }>({})
  const term = q.trim()
  const lines = useQuery({
    queryKey: ['customs', 'broker-lines', term],
    queryFn: () => searchBrokerLines(term),
    enabled: term.length >= 2 && !state.done,
    staleTime: 30_000,
  })

  const bind = async () => {
    if (!row || !picked) return
    setState({ busy: true })
    try {
      const res = await uploadBrokerAnswer({
        brokerRequestId: picked.brokerRequestId,
        rows: [{ ...row, partNumber: picked.partNumber }],
      })
      const n = res.positionsUpdated + (res.filledPositions ?? 0)
      setState({
        done:
          n > 0
            ? `Привязано к ${picked.partNumber} (${picked.fileCode}): ответ лёг на ${n} ${positionsWord(n)}`
            : `Привязано к ${picked.partNumber} (${picked.fileCode}): новых данных для позиций нет`,
      })
      void qc.invalidateQueries({ queryKey: ['customs'] })
    } catch (err) {
      setState({ error: apiErrorText(err, 'Не удалось привязать артикул') })
    }
  }

  return (
    <li className="rounded-lg border border-gray-200 p-2 space-y-1.5">
      <div className="font-medium">{part}</div>
      {state.done ? (
        <div className="text-green-700">{state.done}</div>
      ) : !row ? (
        <div className="text-gray-500">Строки нет в файле — привязать нельзя</div>
      ) : (
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            value={q}
            onChange={(e) => {
              setQ(e.target.value)
              setPicked(null)
            }}
            placeholder="Наш партномер (часть)"
            aria-label={`Поиск партномера для ${part}`}
            className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white sm:w-44"
          />
          <select
            value={picked ? `${picked.brokerRequestId}|${picked.partNumber}` : ''}
            onChange={(e) =>
              setPicked(
                (lines.data ?? []).find(
                  (l) => `${l.brokerRequestId}|${l.partNumber}` === e.target.value,
                ) ?? null,
              )
            }
            aria-label={`Строка запроса для ${part}`}
            className="px-2 py-1 border border-gray-300 rounded-lg text-xs bg-white flex-1 min-w-0"
          >
            <option value="">
              {lines.isFetching
                ? 'Ищу…'
                : (lines.data?.length ?? 0) === 0
                  ? 'Ничего не найдено — уточните поиск'
                  : 'Выберите строку запроса'}
            </option>
            {(lines.data ?? []).map((l) => (
              <option
                key={`${l.brokerRequestId}|${l.partNumber}`}
                value={`${l.brokerRequestId}|${l.partNumber}`}
              >
                {l.partNumber}
                {l.brand ? ` · ${l.brand}` : ''} · {l.fileCode} ·{' '}
                {BROKER_REQUEST_ITEM_STATUS_LABELS_RU[l.status]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void bind()}
            disabled={!picked || state.busy}
            className="px-3 py-1 bg-brand-600 text-white text-xs rounded-lg hover:bg-brand-700 disabled:opacity-50"
          >
            {state.busy ? 'Привязываю…' : 'Привязать'}
          </button>
        </div>
      )}
      {state.error && <div className="text-red-600">{state.error}</div>}
    </li>
  )
}
